import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Header from "@/components/Header";
import useGoogleAnalytics from "@/lib/analytics";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Discover from "@/pages/Discover";
import Matches from "@/pages/Matches";
import PotentialMatches from "@/pages/PotentialMatches";
import TopRated from "@/pages/TopRated";
import RatedByYou from "@/pages/RatedByYou";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";
import ProfileView from "@/pages/ProfileView";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Inbox from "@/pages/Inbox";
import Chat from "@/pages/Chat";
import Admin from "@/pages/Admin";
import Advice from "@/pages/Advice";
import AdviceArticle from "@/pages/AdviceArticle";
import { Loader2 } from "lucide-react";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#722f37]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function GoogleSessionCatcher() {
  const { loginWithGoogleSession } = useAuth();
  const nav = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    // Emergent redirects back to `/#session_id=...`. Do the exchange exactly once,
    // then strip the hash so a refresh doesn't try to re-use a spent id.
    if (handled.current) return;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    handled.current = true;
    const sessionId = decodeURIComponent(m[1]);
    // Clean the hash immediately so the id never ends up in analytics or history state.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    (async () => {
      try {
        const { needs_onboarding } = await loginWithGoogleSession(sessionId);
        toast.success(needs_onboarding ? "Welcome! Please complete your profile." : "Signed in with Google.");
        nav(needs_onboarding ? "/profile?onboard=1" : "/discover", { replace: true });
      } catch (err) {
        toast.error("Google sign-in failed. Please try again.");
        nav("/login", { replace: true });
      }
    })();
  }, [loginWithGoogleSession, nav]);

  return null;
}

function Shell() {
  useGoogleAnalytics();
  return (
    <div className="App">
      <GoogleSessionCatcher />
      <Header />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/advice" element={<Advice />} />
        <Route path="/advice/:slug" element={<AdviceArticle />} />
        <Route path="/discover" element={<RequireAuth><Discover /></RequireAuth>} />
        <Route path="/potential-matches" element={<RequireAuth><PotentialMatches /></RequireAuth>} />
        <Route path="/top-rated" element={<RequireAuth><TopRated /></RequireAuth>} />
        <Route path="/rated-by-you" element={<RequireAuth><RatedByYou /></RequireAuth>} />
        <Route path="/matches" element={<RequireAuth><Matches /></RequireAuth>} />
        <Route path="/inbox" element={<RequireAuth><Inbox /></RequireAuth>} />
        <Route path="/chat/:matchId" element={<RequireAuth><Chat /></RequireAuth>} />
        <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
        <Route path="/pricing" element={<RequireAuth><Pricing /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
        <Route path="/u/:userId" element={<RequireAuth><ProfileView /></RequireAuth>} />
        <Route path="/payment/success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster position="bottom-center" richColors />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AuthProvider>
  );
}
